import { useEffect, useState } from "react";
import { FaFolder, FaProjectDiagram, FaSearch } from "react-icons/fa";
import "../../../styles/studio/folder/addItemModal.css";
import {
  createFolder,
  addItemToFolder,
} from "../../../services/folder.service";
import { getVisualizations } from "../../../services/visualizations.service";
import ErrorToast from "../../common/ErrorToast";

const AddItemModal = ({ folderId, onClose, onSuccess, setError }) => {
  const [step, setStep] = useState(1);
  const [type, setType] = useState(null);

  // Folder creation
  const [folderTitle, setFolderTitle] = useState("");
  const [folderDesc, setFolderDesc] = useState("");
  const [creating, setCreating] = useState(false);

  // Visualization list
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [loadingViz, setLoadingViz] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (step === 2 && type === "visualization") {
      fetchVisualizations();
    }
  }, [step, type, page, search]);

  const fetchVisualizations = async () => {
    setLoadingViz(true);
    try {
      const query = { page, limit: 10 };
      if (search.trim()) query.search = search;

      const res = await getVisualizations(query);
      setRecords(res.records);
      setTotalPages(res.totalPages);
    } catch (err) {
      setErr(err.message);
      setError(err.message);
    } finally {
      setLoadingViz(false);
    }
  };

  const handleCreateFolder = async () => {
    if (!folderTitle.trim()) return;
    setCreating(true);
    try {
      const folder = await createFolder({
        title: folderTitle,
        description: folderDesc,
      });

      await addItemToFolder(folderId, {
        itemId: folder.folderId,
        type: "folder",
      });

      onSuccess?.();
      onClose();
    } catch (err) {
      setErr(err.message);
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleAddVisualization = async (publicationId) => {
    try {
      await addItemToFolder(folderId, {
        itemId: publicationId,
        type: "publication",
      });
      onSuccess?.();
      onClose();
    } catch (err) {
      setErr(err.message);
      setError(err.message);
    }
  };

  return (
    <div className="add-item-modal-backdrop">
      <div className="add-item-modal">
        <ErrorToast error={err} background={false} />
        <div className="add-item-modal-header">
          <h2>Add Item</h2>
          <button onClick={onClose}>✕</button>
        </div>

        {/* STEP 1 */}
        {step === 1 && (
          <div className="add-item-step-select">
            <button
              className="add-item-type-btn"
              onClick={() => {
                setType("folder");
                setStep(2);
              }}
            >
              <FaFolder /> Folder
            </button>

            <button
              className="add-item-type-btn"
              onClick={() => {
                setType("visualization");
                setStep(2);
              }}
            >
              <FaProjectDiagram /> Visualization
            </button>
          </div>
        )}

        {/* STEP 2 — FOLDER */}
        {step === 2 && type === "folder" && (
          <div className="add-item-folder-form">
            <input
              type="text"
              placeholder="Folder title"
              value={folderTitle}
              onChange={(e) => setFolderTitle(e.target.value)}
            />

            <textarea
              placeholder="Folder description (optional)"
              value={folderDesc}
              onChange={(e) => setFolderDesc(e.target.value)}
              rows={3}
            />

            <button onClick={handleCreateFolder} disabled={creating}>
              {creating ? "Creating..." : "Create & Add"}
            </button>
          </div>
        )}

        {/* STEP 2 — VISUALIZATIONS */}
        {step === 2 && type === "visualization" && (
          <div className="add-item-viz-section">
            <div className="add-item-search">
              <FaSearch />
              <input
                placeholder="Search visualizations..."
                value={search}
                onChange={(e) => {
                  setPage(1);
                  setSearch(e.target.value);
                }}
              />
            </div>

            <div className="add-item-viz-list">
              {loadingViz ? (
                <p>Loading...</p>
              ) : (
                records.map((v) => (
                  <div key={v.publicationId} className="add-item-viz-card">
                    <div>
                      <h4>{v.title}</h4>
                      <p>{v.description}</p>
                    </div>
                    <button
                      onClick={() => handleAddVisualization(v.publicationId)}
                    >
                      Add
                    </button>
                  </div>
                ))
              )}
            </div>

            <div className="add-item-pagination">
              <button disabled={page === 1} onClick={() => setPage(page - 1)}>
                Prev
              </button>
              <span>
                {page} / {totalPages}
              </span>
              <button
                disabled={page === totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddItemModal;
